import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const TOOLS = [
  {
    step: 'Diagnose',
    icon: 'Gauge',
    title: 'Contractor Revenue Scorecard',
    description: 'Score lead response, quote follow-up, pricing discipline, reviews and referrals, then identify the weakest stage.',
    detail: 'Best starting point when you are not sure where revenue is leaking.',
    to: '/contractor-revenue-scorecard',
    action: 'Score my workflow',
  },
  {
    step: 'Measure',
    icon: 'Ruler',
    title: 'Fence Measurement Worksheet',
    description: 'Break a project into fence runs, total the footage, track heights, gates, tear-out and field notes.',
    detail: 'Use first when you are turning site measurements into a reliable estimating baseline.',
    to: '/fence-measurement-worksheet',
    action: 'Build measurement worksheet',
  },
  {
    step: 'Plan materials',
    icon: 'Boxes',
    title: 'Fence Material Takeoff Calculator',
    description: 'Estimate sections, posts, rails, pickets and optional concrete quantities from your own spacing and dimensions.',
    detail: 'Use before pricing when you need a fast planning quantity check from the measured fence run.',
    to: '/fence-material-takeoff-calculator',
    action: 'Build material takeoff',
  },
  {
    step: 'Estimate',
    icon: 'Calculator',
    title: 'Fence Estimate Calculator',
    description: 'Calculate selling price from footage, your own per-foot rate, gates, tear-out, other charges and optional tax.',
    detail: 'Useful for quick quote math before moving into a saved customer estimate.',
    to: '/fence-estimate-calculator',
    action: 'Calculate an estimate',
  },
  {
    step: 'Define scope',
    icon: 'FilePenLine',
    title: 'Fence Estimate Scope Builder',
    description: 'Turn footage, fence type, height, gates, removal, inclusions and site notes into a copy-ready scope of work.',
    detail: 'Use when the price math is taking shape but the customer-facing scope still needs structure.',
    to: '/fence-estimate-template',
    action: 'Build the scope',
  },
  {
    step: 'Plan labor',
    icon: 'Users',
    title: 'Fence Labor & Crew Planner',
    description: 'Turn footage and your production assumptions into crew-days, worker-hours and optional direct labor cost.',
    detail: 'Use before margin review or scheduling when labor capacity needs to be explicit.',
    to: '/fence-labor-crew-planner',
    action: 'Plan crew labor',
  },
  {
    step: 'Plan schedule',
    icon: 'CalendarDays',
    title: 'Fence Installation Schedule Planner',
    description: 'Turn crew workdays into a working start, production finish and optional buffered planning finish.',
    detail: 'Use when you need a realistic install window before committing dates to the customer or crew.',
    to: '/fence-install-schedule-planner',
    action: 'Plan install window',
  },
  {
    step: 'Manage changes',
    icon: 'FileDiff',
    title: 'Fence Change Order Planner',
    description: 'Model added or removed scope, revised contract value, internal cost impact and schedule-day changes.',
    detail: 'Use after the job is sold when customer requests or field conditions change the agreed work.',
    to: '/fence-change-order-planner',
    action: 'Plan a change order',
  },
  {
    step: 'Close out',
    icon: 'ClipboardCheck',
    title: 'Fence Final Walkthrough & Closeout Planner',
    description: 'Review finished work, gates, cleanup, punch-list items, customer handoff and final billing readiness.',
    detail: 'Use after production is complete and before treating the job as financially and operationally closed.',
    to: '/fence-final-walkthrough-checklist',
    action: 'Run final walkthrough',
  },
  {
    step: 'Protect margin',
    icon: 'ShieldCheck',
    title: 'Profit Guard',
    description: 'Compare selling price with entered direct costs, gross profit, gross margin, markup and a target-margin price.',
    detail: 'Use after you know the selling price and want to test the job economics.',
    to: '/fence-profit-margin-calculator',
    action: 'Run a profit check',
  },
  {
    step: 'Compare options',
    icon: 'Columns3',
    title: 'Fence Proposal Option Builder',
    description: 'Prepare Good, Better and Best-style choices using only the scopes and selling prices you enter.',
    detail: 'Use when a customer benefits from real alternatives instead of a single take-it-or-leave-it quote.',
    to: '/fence-proposal-option-builder',
    action: 'Build proposal options',
  },
  {
    step: 'Review scope',
    icon: 'ClipboardCheck',
    title: 'Fence Estimate Checklist',
    description: 'Review measurements, gates, site conditions, pricing assumptions and open customer questions before the quote leaves your hands.',
    detail: 'Use before presenting the estimate or when a scope feels incomplete.',
    to: '/fence-estimate-checklist',
    action: 'Review an estimate',
  },
  {
    step: 'Recover quotes',
    icon: 'MessagesSquare',
    title: 'Fence Estimate Follow-Up Guide',
    description: 'Use a practical 1, 3, 7, 14 and 30-day follow-up sequence with copy-ready customer scripts.',
    detail: 'Use after the quote is sent and the customer has not decided yet.',
    to: '/fence-estimate-follow-up',
    action: 'Open follow-up guide',
  },
];

const FAQ = [
  ['Are the PicketOps contractor tools free?', 'Yes. The tools on this page can be used without creating a PicketOps account. The saved contractor workspace is separate.'],
  ['Do the calculators use market prices?', 'No. The estimate calculator is designed for your own rates, and Profit Guard uses only the selling price, costs and target you enter.'],
  ['Do these tools save customer information?', 'No. The public tools do not create customer, estimate, job or payment records. Saved records are created only inside the authenticated PicketOps workspace.'],
  ['Which tool should I start with?', 'If you are unsure where the business is leaking revenue, start with the Contractor Revenue Scorecard. If a real fence job is already in front of you, start with the Measurement Worksheet, then continue to Material Takeoff and the Estimate Calculator.'],
];

export default function FenceContractorTools() {
  const { isAuthenticated } = useAuth();
  const workspaceTarget = isAuthenticated ? '/dashboard' : '/contractor/signup';
  const [search, setSearch] = useState('');
  const query = search.trim().toLowerCase();
  const visibleTools = TOOLS.map((tool, index) => ({ ...tool, number: index + 1 })).filter(tool => !query || [tool.title, tool.step, tool.description, tool.detail].join(' ').toLowerCase().includes(query));

  return (
    <div className="picketops-public min-h-svh bg-background text-foreground">
      <header className="max-w-6xl mx-auto px-4 pt-20 pb-6 sm:pr-32">
        <Link to="/" aria-label="PicketOps home" className="inline-flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span aria-hidden="true" className="flex size-10 items-center justify-center rounded-md bg-primary text-primary-foreground font-heading text-xl font-bold">P</span>
          <span>
            <span className="block font-heading text-xl font-bold tracking-tight">PICKETOPS</span>
            <span className="block text-xs text-muted-foreground">Revenue OS for fence contractors</span>
          </span>
        </Link>
        <nav aria-label="Breadcrumb" className="mt-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Home</Link>
          <span aria-hidden="true"> / </span>
          <span aria-current="page">Free fence contractor tools</span>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 pb-16 space-y-12">
        <section className="picketops-hero overflow-hidden rounded-3xl border border-primary/20 px-6 py-10 shadow-(--shadow-md) sm:px-8 lg:px-10 lg:py-14">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Free PicketOps contractor toolbox</p>
            <h1 className="mt-4 text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-tight">Free tools for fence estimating, operations, scheduling, change management and closeout</h1>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Use the same operating ideas behind PicketOps without creating an account. Diagnose, measure, estimate, plan labor, schedule work, manage changes, protect margin, close out finished jobs and keep customer follow-up visible.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild><Link to="/contractor-revenue-scorecard">Start with the revenue scorecard <ApperIcon name="ArrowRight" size={16}/></Link></Button>
              <Button variant="outline" asChild><Link to={workspaceTarget}>{isAuthenticated ? 'Open PicketOps workspace' : 'Create account'}</Link></Button>
            </div>
          </div>
        </section>

        <section aria-labelledby="tool-path-heading" className="space-y-6">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">A practical order</p>
            <h2 id="tool-path-heading" className="mt-2 text-3xl font-heading font-semibold">Move from “where is the leak?” to “what do I do next?”</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">You do not need to use every tool. Follow the stage that matches the work currently on your desk.</p>
          </div>

          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 sm:p-6">
            <Label htmlFor="tool-search" className="text-base font-semibold">Find the tool for your next task</Label>
            <p id="tool-search-help" className="mt-2 text-sm text-muted-foreground">Search by task, such as materials, crew, margin or closeout. These tools work without an account.</p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <Input id="tool-search" type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search all 13 tools" aria-describedby="tool-search-help tool-search-count" className="min-h-11 bg-background" maxLength={120}/>
              {search && <Button variant="outline" onClick={() => setSearch('')} className="min-h-11 shrink-0">Clear search</Button>}
            </div>
            <p id="tool-search-count" role="status" aria-live="polite" aria-atomic="true" className="mt-3 text-sm text-muted-foreground">{visibleTools.length} of {TOOLS.length} tools shown{query ? ' for your search' : ' in workflow order'}.</p>
          </div>
          {visibleTools.length === 0 && <div className="rounded-xl border border-dashed border-border p-6 text-center"><h3 className="font-heading text-xl font-semibold">No matching tools</h3><p className="mt-2 text-sm text-muted-foreground">Try a shorter task name or clear the search to browse every tool.</p></div>}
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {visibleTools.map(tool => (
              <Link key={tool.to} to={tool.to} className="group relative flex min-h-64 flex-col rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-(--shadow-md) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <div className="flex items-center justify-between gap-3">
                  <span className="flex size-11 items-center justify-center rounded-lg bg-primary/10 text-primary"><ApperIcon name={tool.icon} size={21}/></span>
                  <span className="text-xs font-bold tabular-nums text-muted-foreground">{String(tool.number).padStart(2, '0')}</span>
                </div>
                <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-primary">{tool.step}</p>
                <h3 className="mt-2 text-xl font-heading font-semibold">{tool.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{tool.description}</p>
                <p className="mt-3 flex-1 text-xs leading-relaxed text-muted-foreground">{tool.detail}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary">{tool.action}<ApperIcon name="ArrowRight" size={15} className="transition-transform group-hover:translate-x-0.5"/></span>
              </Link>
            ))}
          </div>
        </section>

        <section className="grid overflow-hidden rounded-2xl border border-border bg-card shadow-(--shadow-sm) lg:grid-cols-[0.9fr_1.1fr]">
          <div className="bg-primary/5 p-6 sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">From free tool to operating workflow</p>
            <h2 className="mt-2 text-3xl font-heading font-semibold">The public tools do the math. PicketOps keeps the job moving.</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              When the calculation becomes a real customer opportunity, the authenticated workspace connects the lead, saved estimate, proposal, Quote Rescue follow-up, job schedule, production handoff and billing record.
            </p>
            <Button asChild className="mt-6"><Link to="/fence-contractor-software">See the full fence contractor workflow <ApperIcon name="ArrowRight" size={16}/></Link></Button>
          </div>
          <div className="p-6 sm:p-8">
            <ol className="space-y-3">
              {[
                ['Lead', 'Customer and job context'],
                ['Estimate', 'Saved quote using the price book'],
                ['Proposal', 'Options and in-person acceptance'],
                ['Quote Rescue', 'Follow-up dates and customer drafts'],
                ['Job + Production', 'Schedule, crew handoff and materials'],
                ['Money', 'Invoices, balances and recorded payments'],
              ].map(([title, detail], index) => (
                <li key={title} className="flex items-center gap-3 rounded-lg border border-border bg-background p-3">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">{index + 1}</span>
                  <div><p className="text-sm font-semibold">{title}</p><p className="mt-0.5 text-xs text-muted-foreground">{detail}</p></div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section aria-labelledby="tools-faq-heading" className="max-w-4xl mx-auto">
          <h2 id="tools-faq-heading" className="text-2xl font-heading font-semibold">Free contractor tool questions</h2>
          <div className="mt-5 divide-y rounded-xl border border-border bg-card px-5">
            {FAQ.map(([question, answer]) => (
              <details key={question} className="py-4">
                <summary className="cursor-pointer rounded-md font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{question}</summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{answer}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export const route = { path: '/fence-contractor-tools', layout: 'public' };
